import type { CSSProperties, ReactNode } from 'react';

/**
 * The docked right-hand panel beside the chat column — ONE implementation of
 * the layout and entrance animation shared by the builder's record panel and
 * the chat's docked proposal pane, so the two can never drift apart
 * (FRONTEND_PATTERNS § "component composition for visual consistency").
 *
 * Layout: a `shrink-0` flex sibling at a fixed px width, `lg:`-only — the
 * host row's chat column gives up exactly this width (see ReasoningChat's
 * `mainAreaWidth`).
 *
 * Entrance: `pane-slide-in` (index.css) grows the panel 0 → its width on the
 * same 300ms ease-out the chat column's width transition uses, so the pair
 * is complementary for the whole open — the panel rides in smoothly instead
 * of popping to full width against a still-shrinking chat column. Runs once
 * on mount; width changes afterwards (drag-resize) don't replay it.
 *
 * MUST render as a child of the same flex row as the chat column — the
 * element wrapping that row is `display: block`, so a sibling placed after
 * it stacks BELOW the full-height chat column and is clipped away by
 * `overflow-hidden`.
 */
export function DockedSidePanel({
  width,
  children,
  resizeHandle,
  'data-component': dataComponent,
  'data-instance': dataInstance,
}: {
  /** Panel width in px (the shared slideout width). */
  width: number;
  children: ReactNode;
  /**
   * Chrome slot for an `EdgeResizeHandle` (edge='left') on the panel's chat
   * side. The panel is the positioned ancestor the handle anchors to, so it
   * renders here — as a sibling of the scrolling content, never inside it.
   */
  resizeHandle?: ReactNode;
  /** E2E handle for the concrete panel (see frontend/e2e/HANDLES.md). */
  'data-component'?: string;
  'data-instance'?: string;
}) {
  return (
    <div
      className='relative hidden lg:flex shrink-0 h-full flex-col border-l border-border-secondary bg-bg-primary animate-pane-slide-in'
      // Width is a runtime value (user-resizable), so it cannot be a static
      // Tailwind class; the CSS var feeds the entrance keyframe's target.
      style={
        {
          width: `${width}px`,
          '--pane-target-width': `${width}px`,
        } as CSSProperties
      }
      data-component={dataComponent}
      data-instance={dataInstance}
    >
      {resizeHandle}
      {children}
    </div>
  );
}
